import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { FiMenu, FiX, FiMoon, FiSun, FiMessageCircle, FiSettings, FiUser } from 'react-icons/fi'
import { useLocation, useNavigate } from 'react-router-dom'
import Logo from './Logo'
import Button from './ui/Button'
import AuthModal from './AuthModal'
import ProfileMenu from './ProfileMenu'
import { hideProducts } from '../config/featureFlags'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('home')
  const [scrolled, setScrolled] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { user, logout } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
      
      const sections = hideProducts
        ? ['home', 'services', 'about', 'solutions', 'pricing', 'faq', 'contact']
        : ['home', 'services', 'about', 'solutions', 'products', 'pricing', 'faq', 'contact']
      const scrollPosition = window.scrollY + 150

      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  const scrollToSection = (sectionId) => {
    const doScroll = (tries = 0) => {
      const element = document.getElementById(sectionId)
      if (!element) {
        if (tries < 20) {
          requestAnimationFrame(() => doScroll(tries + 1))
        }
        return
      }
      const headerOffset = 84
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      setActiveSection(sectionId)
      setIsMenuOpen(false)
    }

    if (location.pathname !== '/') {
      navigate('/', { replace: false })
      requestAnimationFrame(() => doScroll())
      return
    }

    doScroll()
  }

  const navLinks = [
    { id: 'home', label: 'Início' },
    { id: 'services', label: 'Serviços' },
    { id: 'about', label: 'Sobre' },
    { id: 'solutions', label: 'Soluções' },
    ...(!hideProducts ? [{ id: 'products', label: 'Produtos' }] : []),
    { id: 'pricing', label: 'Planos' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contato' },
  ]

  return (
    <>
      <header 
        className={`
          fixed top-0 left-0 right-0 z-50 transition-theme
          ${scrolled 
            ? 'bg-white/[0.98] dark:bg-gray-900/[0.98] backdrop-blur-2xl shadow-lg border-b border-gray-200/50 dark:border-gray-800/50' 
            : 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/30 dark:border-gray-800/30'
          }
        `}
      >
        <nav className="container-custom py-4 sm:py-5 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center hover:opacity-80 transition-opacity focus:outline-none rounded-lg p-1"
            aria-label="Ir para o início"
          >
            <Logo className="h-8 sm:h-10 md:h-11 w-auto" />
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`
                  relative px-4 py-2.5 rounded-xl 
                  font-bold text-sm tracking-tight
                  transition-all duration-300 ease-out
                  min-h-[44px] flex items-center
                  focus:outline-none
                  ${
                    activeSection === link.id
                      ? 'text-accent font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:text-accent dark:hover:text-accent'
                  }
                `}
                aria-current={activeSection === link.id ? 'page' : undefined}
              >
                {link.label}
                <span 
                  className={`absolute bottom-0 left-1/2 h-0.5 bg-accent rounded-full transition-all duration-300 ease-out ${
                    activeSection === link.id ? 'w-8 -translate-x-1/2 opacity-100' : 'w-0 opacity-0 -translate-x-1/2'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* CTA Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={() => scrollToSection('contact')}
              variant="secondary"
              size="sm"
              className="gap-2"
            >
              <FiMessageCircle className="w-4 h-4" />
              Fale Conosco
            </Button>
            {user ? (
              <ProfileMenu />
            ) : (
              <Button
                onClick={() => setShowAuthModal(true)}
                variant="primary"
                size="sm"
                className="gap-2"
              >
                <FiUser className="w-4 h-4" />
                Entrar
              </Button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="
                p-2.5 rounded-xl 
                border border-gray-200 dark:border-gray-700 
                hover:border-accent dark:hover:border-accent 
                transition-all duration-200
                bg-white dark:bg-gray-800
                hover:bg-gray-50 dark:hover:bg-gray-700
                focus:outline-none
                min-h-[44px] min-w-[44px] flex items-center justify-center
              "
              aria-label={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            >
              {theme === 'dark' ? (
                <FiSun className="w-5 h-5 text-yellow-500" />
              ) : (
                <FiMoon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="
                lg:hidden 
                p-2.5 rounded-xl 
                border border-gray-200 dark:border-gray-700 
                hover:border-accent dark:hover:border-accent 
                transition-all duration-200
                bg-white dark:bg-gray-800
                hover:bg-gray-50 dark:hover:bg-gray-700
                focus:outline-none
                min-h-[44px] min-w-[44px] flex items-center justify-center
              "
              aria-label="Abrir menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <FiX className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <FiMenu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer */}
      <div
        className={`
          fixed inset-0 z-[60] lg:hidden
          transition-opacity duration-300 ease-out
          ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        aria-hidden={!isMenuOpen}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
          aria-label="Fechar menu"
        />

        {/* Drawer */}
        <aside
          className={`
            absolute top-0 right-0 h-full w-full max-w-sm
            bg-white dark:bg-gray-900
            shadow-2xl
            transform transition-transform duration-300 ease-out
            ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          `}
          aria-label="Menu de navegação"
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <Logo className="h-10 w-auto" />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="
                  p-2 rounded-lg
                  hover:bg-gray-100 dark:hover:bg-gray-800
                  transition-colors
                  focus:outline-none
                  min-h-[44px] min-w-[44px] flex items-center justify-center
                "
                aria-label="Fechar menu"
              >
                <FiX className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-6">
              <ul className="space-y-2">
                {navLinks.map((link, index) => (
                  <li
                    key={link.id}
                    className={`transition-all duration-300 ease-out ${
                      isMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                    }`}
                    style={{ transitionDelay: isMenuOpen ? `${index * 50}ms` : '0ms' }}
                  >
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className={`
                        w-full text-left px-5 py-4 rounded-xl
                        font-medium text-base
                        transition-all duration-200
                        min-h-[52px] flex items-center
                        focus:outline-none
                        ${
                          activeSection === link.id
                            ? 'text-accent font-semibold bg-accent/10 dark:bg-accent/20'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }
                      `}
                      aria-current={activeSection === link.id ? 'page' : undefined}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-6 space-y-3">
                <Button
                  onClick={() => {
                    scrollToSection('contact')
                    setIsMenuOpen(false)
                  }}
                  variant="secondary"
                  size="lg"
                  className="w-full gap-2"
                >
                  <FiMessageCircle className="w-4 h-4" />
                  Fale Conosco
                </Button>
                {user ? (
                  <>
                    <Button
                      onClick={() => {
                        navigate('/account')
                        setIsMenuOpen(false)
                      }}
                      variant="primary"
                      size="lg"
                      className="w-full gap-2"
                    >
                      <FiSettings className="w-4 h-4" />
                      Minha conta
                    </Button>
                    <Button
                      onClick={() => {
                        logout()
                        setIsMenuOpen(false)
                      }}
                      variant="ghost"
                      size="lg"
                      className="w-full gap-2"
                    >
                      <FiUser className="w-4 h-4" />
                      Sair
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => {
                      setShowAuthModal(true)
                      setIsMenuOpen(false)
                    }}
                    variant="primary"
                    size="lg"
                    className="w-full gap-2"
                  >
                    <FiUser className="w-4 h-4" />
                    Entrar
                  </Button>
                )}
              </div>

              {/* Theme Toggle */}
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
                <button
                  onClick={toggleTheme}
                  className="
                    w-full flex items-center justify-between
                    px-5 py-4 rounded-xl
                    font-medium text-base
                    text-gray-700 dark:text-gray-300
                    hover:bg-gray-100 dark:hover:bg-gray-800
                    transition-colors
                    focus:outline-none
                    min-h-[52px]
                  "
                >
                  <span>{theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}</span>
                  {theme === 'dark' ? (
                    <FiSun className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <FiMoon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </nav>
          </div>
        </aside>
      </div>

      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} />
      )}
    </>
  )
}

export default Header
